"use client";

import React, { useState } from "react";
import Image from "next/image";
import SmoothImage from "@/utils/smoothImage";
import { motion, AnimatePresence } from "framer-motion";
import { PROJECT_INFO } from "@/types/projectConsts";
import SharedContactForm from "@/components/Forms/SharedContactForm";

export type INFO = {
  title: string;
  description1: string;
  title2: string;
  description2: string;
  title3: string;
  description3: string;
  phone: string;
  mail: string;
  webUrl: string;
  fb: string;
  youtube?: string;
};

interface FooterContactProps {
  INFO: INFO;
  reasonsList?: Array<{ title: string; desc: string }>;
  registerInfo?: {
    title?: string;
    subtitle?: string;
    benefits?: string[];
    calloutTitle?: string;
    calloutDesc?: string;
    hotline?: string;
    note?: string;
  };
  bgImageUrl?: string;
  apptOptions?: Array<{ label: string; value: string }>;
  formTitle?: string;
  formSubtitle?: string;
  partners?: Array<{ name: string; logo: string }>;
  partnersLabel?: string;
  addressLabel?: string;
  contactLabel?: string;
  reasonsTitle?: string;
  reasonsSubtitle?: string;
  benefitsTitle?: string;
  copyrightText?: string;
  logoUrl?: string;
  brandLogos?: Array<{ name?: string; logo: string }>;
}

export default function FooterContact({
  INFO,
  reasonsList = [],
  registerInfo,
  bgImageUrl,
  apptOptions = [
    { label: "1PN", value: "1PN" },
    { label: "2PN", value: "2PN" },
    { label: "Studio", value: "Studio" },
  ],
  partners = [],
  partnersLabel = "ĐỐI TÁC DỰ ÁN",
  addressLabel = "ĐỊA CHỈ DỰ ÁN",
  contactLabel = "THÔNG TIN LIÊN HỆ",
  reasonsTitle = "ĐẶC QUYỀN SỞ HỮU",
  reasonsSubtitle = "VÌ SAO NÊN XUỐNG TIỀN NGAY HÔM NAY?",
  benefitsTitle = "Đăng ký ngay hôm nay để nhận:",
  copyrightText = "©2026 Copyright by ZenGroup. All rights reserved.",
  logoUrl,
  brandLogos,
}: FooterContactProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (!INFO) return null;

  const phoneRaw = PROJECT_INFO.hotline.replace(/\s+/g, "");

  const toggleReason = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  return (
    <footer id="contact" className="relative w-full overflow-hidden bg-white">
      {/* ── MAIN FOOTER (Full width with phoi_canh_3.png background) ── */}
      <div className="relative py-12 md:py-16 w-full overflow-hidden">
        {/* Background Image */}
        <div className="inset-0 -z-10 pointer-events-none">
          <SmoothImage
            src={bgImageUrl || "/images/phoi_canh_3.png"}
            alt="footer bg"
            fill
            sizes="100vw"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#151311]/70 backdrop-blur-sm" />
        </div>

        <div className="w-full px-4 md:px-12 lg:px-[6%] xl:px-[5%] 2xl:px-[10%] relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-stretch">
            {/* Card 1: Contact Info */}
            <div className="bg-white border-t-4 border-primary p-8 md:p-10 shadow-lg flex flex-col justify-between relative overflow-hidden ">
              <div className="space-y-4">
                {/* BRAND LOGOS LINE */}
                <div className="flex items-center justify-between gap-1 w-full">
                  {/* Main Brand Logo (30% width of Card 1) */}
                  <div className="w-[36%] flex items-center justify-start shrink-0">
                    <SmoothImage
                      src={logoUrl || "/logos/logo_transparent.png"}
                      alt="brand logo"
                      width={180}
                      height={60}
                      sizes="(max-width: 640px) 120px, 180px"
                      className="w-full max-w-[180px] h-auto object-contain"
                      priority
                    />
                  </div>

                  {/* 2 Additional logos sharing remaining 70% width (50% each) */}
                  <div className="w-[64%] grid grid-cols-2 gap-1 items-start">
                    {[
                      ...(brandLogos && brandLogos.length > 0
                        ? brandLogos.slice(0, 2)
                        : []),
                    ].map((b, idx) => (
                      <div
                        key={idx}
                        className="flex flex-col items-center group cursor-pointer w-full"
                        title={b.name}
                      >
                        <div className="relative w-full aspect-square mx-auto bg-white flex items-center justify-center p-0.5 transition duration-300 rounded">
                          <div className="relative w-full h-full">
                            <SmoothImage
                              src={b.logo}
                              alt={b.name || `brand logo ${idx + 2}`}
                              fill
                              sizes="120px"
                              className="object-contain"
                            />
                          </div>
                        </div>
                        {b.name && (
                          <span className="text-[9px] sm:text-[10px] text-primary/80 font-semibold text-center line-clamp-2 leading-tight">
                            {b.name}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* PARTNERS LOGOS */}
                {partners && partners.length > 0 && (
                  <div className="pb-4">
                    <p className="font-bold text-secondary-hover text-[9px] mb-1 uppercase tracking-[0.1em]">
                      {partnersLabel}
                    </p>
                    <div className="grid grid-cols-4 gap-1">
                      {partners.map((p, idx) => (
                        <div
                          key={idx}
                          className="flex flex-col items-center group cursor-pointer w-full"
                          title={p.name}
                        >
                          <div className="relative w-full aspect-square mx-auto bg-white flex items-center justify-center p-0.5 transition duration-300 rounded">
                            <div className="relative w-full h-full">
                              {p.logo && (
                                <SmoothImage
                                  src={p.logo}
                                  alt={p.name}
                                  fill
                                  sizes="120px"
                                  className="object-contain"
                                  loading="eager"
                                />
                              )}
                            </div>
                          </div>
                          {p.name && (
                            <span className="text-[9px] sm:text-[10px] text-primary/80 font-semibold mt-1 text-center line-clamp-2 leading-tight">
                              {p.name}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px] text-primary mt-4">
                  <div>
                    <p className="font-bold text-secondary-hover text-[9px] mb-1">
                      {addressLabel}
                    </p>
                    <p className="text-[10px] leading-relaxed text-primary font-semibold text-justify">
                      {PROJECT_INFO.address}
                    </p>
                  </div>

                  <div>
                    <div className="font-bold text-secondary-hover text-[9px] mb-1">
                      {contactLabel}
                    </div>
                    <div className="text-[10px] space-y-0.5 font-semibold text-primary">
                      <p>
                        <span className="text-primary">Hotline:</span>{" "}
                        <a
                          href={`tel:${phoneRaw}`}
                          className="hover:underline font-bold text-primary"
                        >
                          {PROJECT_INFO.hotline}
                        </a>
                      </p>
                      <p>
                        <span className="text-primary">Email:</span>{" "}
                        <a
                          href={`mailto:${PROJECT_INFO.email}`}
                          className="hover:underline font-bold text-primary truncate block"
                        >
                          {PROJECT_INFO.email}
                        </a>
                      </p>
                      <p>
                        <span className="text-primary">Website:</span>{" "}
                        <a
                          href={`https://${PROJECT_INFO.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline font-bold text-primary"
                        >
                          {PROJECT_INFO.website}
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* DESCRIPTION + PARTNERS + SOCIAL */}
              <div className="space-y-4">
                <div className="flex gap-4 pt-1">
                  <a
                    href={INFO.fb}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="opacity-75 hover:opacity-100 transition duration-200"
                  >
                    <img
                      src="/icons/fb_icon_blue.svg"
                      className="w-5 h-5"
                      alt="fb"
                    />
                  </a>
                  <a
                    href={
                      INFO.youtube ||
                      "https://www.youtube.com/@OneVisionGroup-VietNam"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="opacity-75 hover:opacity-100 transition duration-200"
                  >
                    <img
                      src="/icons/youtube_icon_red.svg"
                      className="w-5 h-5"
                      alt="youtube"
                    />
                  </a>
                </div>

                {/* DIVIDER */}
                <div className="h-px bg-primary/10" />

                {/* COPYRIGHT */}
                <div className="text-[9px] text-primary/50 font-bold">
                  {copyrightText}
                </div>
              </div>
            </div>

            {/* Card 2: Reasons to Buy */}
            <div className="bg-white border-t-4 border-primary p-8 md:p-10 shadow-lg  relative overflow-hidden ">
              <div className="space-y-4">
                <span className="text-primary text-[10px] font-black tracking-widest uppercase block">
                  {reasonsTitle}
                </span>
                <h4 className="text-base md:text-lg font-black text-secondary-hover leading-snug uppercase">
                  {reasonsSubtitle}
                </h4>

                <div className="space-y-1 pt-2">
                  {reasonsList.map((item, idx) => {
                    const isOpen = openIndex === idx;
                    return (
                      <div key={idx} className="border-b border-gray-100 pb-1">
                        <button
                          onClick={() => toggleReason(idx)}
                          className="w-full text-left py-2 flex justify-between items-center focus:outline-none group/btn"
                        >
                          <span className="font-bold text-[10px] uppercase tracking-wide text-primary">
                            {item.title}
                          </span>
                          <span
                            className={`text-sm font-light transition-transform duration-500 ease-in-out ml-4 ${
                              isOpen
                                ? "rotate-[135deg] text-primary"
                                : "text-gray-400 group-hover/btn:rotate-[360deg] group-hover/btn:text-primary"
                            }`}
                          >
                            +
                          </span>
                        </button>
                        <AnimatePresence initial={false}>
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.25, ease: "easeInOut" }}
                              className="overflow-hidden"
                            >
                              <p className="pb-3 text-gray-500 text-[10px] font-light leading-normal text-justify">
                                {item.desc}
                              </p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>

                <div className="text-[10px] text-primary/75 leading-relaxed font-medium text-justify pt-2">
                  {INFO.description3 || ""}
                </div>
              </div>
            </div>

            {/* Card 3: Inline Form */}
            <div className="bg-gradient-to-br from-primary to-primaryDark border-t-4 border-secondary p-8 md:p-10 shadow-lg flex flex-col justify-between">
              <div className="space-y-2">
                <span className="text-secondary text-[9px] font-bold uppercase tracking-[0.2em] block">
                  {registerInfo?.title || "ĐĂNG KÝ NHẬN THÔNG TIN ƯU TIÊN"}
                </span>
                <h4 className="text-sm font-black text-white leading-snug uppercase">
                  {registerInfo?.subtitle ||
                    "Đừng bỏ lỡ cơ hội sở hữu Dragon Home ở giai đoạn đầu"}
                </h4>

                <div className="text-[10px] space-y-1 font-semibold text-white/80">
                  <p className="mb-0.5 text-white font-bold">{benefitsTitle}</p>
                  {registerInfo?.benefits?.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-1.5">
                      <p>{item}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-white/10 border border-white/10 rounded-xl backdrop-blur-sm mt-4">
                <SharedContactForm
                  theme="dark"
                  size="compact"
                  apptOptions={apptOptions}
                  submitButtonText="Nhận báo giá"
                  nguon="Form liên hệ chân trang (FooterContact)"
                  useDropdown={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
